/**
 * Arrange phase store for registry-demo.
 * 
 * SUPABASE-ONLY PERSISTENCE for snapshots:
 * - Snapshots are persisted to priority_snapshots table
 * - UI state (selectedTool, toolConfig, lens) is ephemeral
 */

import { create } from "zustand";
import { v4 as uuidv4 } from 'uuid';
import type { ToolConfig, Lens } from "../../harmonizer/types";
import {
  getSafeSupabaseClient,
  getCurrentWorkspaceId,
  isSupabaseAvailable,
  generateUUID,
} from '../../lib/supabaseSync';
import { useSessionReportStore } from './sessionReportStore';

const TABLE_NAME = 'priority_snapshots';

// Database snapshot record
interface DbPrioritySnapshot {
  id: string;
  session_id: string | null;
  cycle_id: string | null;
  focus_list_id: string | null;
  tool_name: string;
  tool_config: ToolConfig;
  lens: Lens | null;
  ranked_items: unknown[];
  scores: Record<string, unknown>;
  cut_line_position: number | null;
  created_at: string;
  created_by: string | null;
  workspace_id: string | null;
}

/**
 * The arrange tool's persisted snapshot shape (priority_snapshots table).
 *
 * NOTE: this is a distinct concept from platform's `PrioritySnapshot`
 * (src/platform/priorityTruth.ts — snapshotId/sessionId/toolName/createdAt:
 * number/scoreSet). The two types previously shared a name via a mis-import,
 * which masked a real bug: callers that hand this store a canonical
 * PrioritySnapshot (e.g. ToolRunner via sessionApi.runToolOnActive) don't
 * carry rankedItems/toolConfig/lens/etc., and createdAt can arrive as a
 * number — which crashed the `.toISOString()` calls below. Kept most fields
 * optional to reflect what's actually guaranteed at runtime.
 */
export interface ArrangeSnapshot {
  id?: string;
  sessionId?: string;
  cycleId?: string;
  focusListId?: string;
  toolName: string;
  toolConfig?: ToolConfig;
  lens?: Lens | null;
  rankedItems?: unknown[];
  scores?: Record<string, unknown>;
  cutLinePosition?: number;
  createdAt?: Date | number | string;
  createdBy?: string;
  overrideOf?: string;
  overrideRationale?: string;
}

// Coerce a Date | number | string | undefined into a real Date (defensive:
// upstream producers of this "snapshot" concept disagree on shape/type).
function toDate(value: Date | number | string | undefined): Date {
  if (!value) return new Date();
  return value instanceof Date ? value : new Date(value);
}

// Convert DB row to ArrangeSnapshot
function dbToSnapshot(row: DbPrioritySnapshot): ArrangeSnapshot {
  return {
    id: row.id,
    sessionId: row.session_id || undefined,
    cycleId: row.cycle_id || undefined,
    focusListId: row.focus_list_id || undefined,
    toolName: row.tool_name,
    toolConfig: row.tool_config,
    lens: row.lens || undefined,
    rankedItems: row.ranked_items,
    scores: row.scores,
    cutLinePosition: row.cut_line_position || undefined,
    createdAt: new Date(row.created_at),
    createdBy: row.created_by || undefined,
  };
}

// Convert ArrangeSnapshot to DB row
function snapshotToDb(snapshot: ArrangeSnapshot, workspaceId: string | null): DbPrioritySnapshot {
  return {
    id: snapshot.id || uuidv4(),
    session_id: snapshot.sessionId || null,
    cycle_id: snapshot.cycleId || null,
    focus_list_id: snapshot.focusListId || null,
    tool_name: snapshot.toolName,
    tool_config: snapshot.toolConfig || {},
    lens: snapshot.lens || null,
    ranked_items: snapshot.rankedItems || [],
    scores: snapshot.scores || {},
    cut_line_position: snapshot.cutLinePosition || null,
    created_at: toDate(snapshot.createdAt).toISOString(),
    created_by: snapshot.createdBy || null,
    workspace_id: workspaceId,
  };
}

interface ArrangeState {
  // UI state (ephemeral)
  selectedTool: string | null;
  setSelectedTool: (toolName: string | null) => void;
  
  toolConfig: ToolConfig;
  setToolConfig: (config: ToolConfig) => void;
  
  lens: Lens | null;
  setLens: (lens: Lens | null) => void;
  
  lastToolResult: {
    snapshot: ArrangeSnapshot;
    timestamp: number;
  } | null;
  setLastToolResult: (result: { snapshot: ArrangeSnapshot; timestamp: number } | null) => void;

  // Persisted snapshots
  snapshots: ArrangeSnapshot[];
  isLoading: boolean;
  error: string | null;

  // Supabase sync
  fetchFromSupabase: (sessionId?: string, cycleId?: string) => Promise<void>;
  addSnapshot: (snapshot: ArrangeSnapshot, userId?: string) => Promise<{ success: boolean; error?: string }>;
  createRankingOverride: (params: {
    originalSnapshotId: string;
    newRankedItems: unknown[];
    rationale: string;
    overriddenBy: string;
    cycleId?: string;
  }) => Promise<{ success: boolean; newSnapshot?: ArrangeSnapshot; error?: string }>;
  clearSnapshots: () => void;
}

export const useArrangeStore = create<ArrangeState>((set, get) => ({
  selectedTool: null,
  setSelectedTool: (toolName: string | null) => set({ selectedTool: toolName }),
  
  toolConfig: {},
  setToolConfig: (config: ToolConfig) => set({ toolConfig: config }),
  
  lens: null,
  setLens: (lens: Lens | null) => set({ lens }),
  
  lastToolResult: null,
  setLastToolResult: (result) => set({ lastToolResult: result }),
  
  snapshots: [],
  isLoading: false,
  error: null,
  
  // Fetch snapshots from Supabase
  fetchFromSupabase: async (sessionId?: string, cycleId?: string) => {
    if (!isSupabaseAvailable()) {
      console.log('[ArrangeStore] Supabase not available');
      return;
    }
    
    set({ isLoading: true, error: null });
    
    try {
      const supabase = getSafeSupabaseClient();
      if (!supabase) return;
      
      let query = supabase
        .from(TABLE_NAME)
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50); // Limit to recent snapshots
      
      if (sessionId) {
        query = query.eq('session_id', sessionId);
      }
      
      if (cycleId) {
        query = query.eq('cycle_id', cycleId);
      }
      
      const { data, error } = await query;
      
      if (error) {
        console.error('[ArrangeStore] Fetch error:', error);
        set({ isLoading: false, error: error.message });
        return;
      }
      
      const snapshots = (data || []).map(row => dbToSnapshot(row as DbPrioritySnapshot));
      
      set({ snapshots, isLoading: false, error: null });
      console.log(`[ArrangeStore] Loaded ${snapshots.length} snapshots`);
    } catch (err) {
      console.error('[ArrangeStore] Fetch exception:', err);
      set({ isLoading: false, error: err instanceof Error ? err.message : 'Unknown error' });
    }
  },
  
  /**
   * Add snapshot and persist to Supabase
   * Per Doc 04: Ranking sessions persist tool snapshots
   */
  addSnapshot: async (snapshot: ArrangeSnapshot, userId?: string) => {
    // Validate: focusListId is required for ranking snapshots per Doc 04
    if (!snapshot.focusListId) {
      console.warn('[ArrangeStore] Snapshot created without focusListId - Item Pool should be selected');
    }

    // Ensure snapshot has an ID and a real Date (upstream may hand us a
    // number timestamp — see ArrangeSnapshot doc comment above). Left
    // un-annotated so createdAt narrows to `Date` (not the wider union) for
    // the .toISOString() call below.
    const snapshotWithId = {
      ...snapshot,
      id: snapshot.id || uuidv4(),
      createdAt: toDate(snapshot.createdAt),
    };
    
    // Add to local state
    set((state) => ({
      snapshots: [snapshotWithId, ...state.snapshots],
      lastToolResult: { snapshot: snapshotWithId, timestamp: Date.now() },
    }));
    
    // Persist to Supabase
    if (isSupabaseAvailable()) {
      try {
        const supabase = getSafeSupabaseClient();
        if (!supabase) return { success: false, error: 'Supabase not available' };
        
        const workspaceId = getCurrentWorkspaceId();
        const dbRecord = snapshotToDb(snapshotWithId, workspaceId);
        
        const { error } = await supabase
          .from(TABLE_NAME)
          .insert(dbRecord);
        
        if (error) {
          console.error('[ArrangeStore] Failed to save snapshot:', error);
          return { success: false, error: error.message };
        }
        
        console.log('[ArrangeStore] Saved snapshot:', snapshotWithId.id);

        // Emit governance event for ranking completion (non-blocking)
        if (snapshot.cycleId) {
          supabase.from('governance_events').insert({
            id: generateUUID(),
            cycle_id: snapshot.cycleId,
            event_type: 'ranking_completed',
            actor_id: userId || snapshot.createdBy || 'system',
            occurred_at: snapshotWithId.createdAt.toISOString(),
            note: JSON.stringify({
              snapshotId: snapshotWithId.id,
              toolName: snapshot.toolName,
              itemCount: snapshot.rankedItems?.length || 0,
              focusListId: snapshot.focusListId,
            }),
          }).then(({ error: govError }) => {
            if (govError) {
              console.warn('[ArrangeStore] Governance event failed (non-blocking):', govError.message);
            }
          });
          
          // Create session report for ranking per Doc 07 (non-blocking)
          const ranking = (snapshot.rankedItems || []).map((item, index) => ({
            itemId: typeof item === 'string' ? item : (item as any).itemId || (item as any).id,
            rank: index + 1,
            score: snapshot.scores?.[typeof item === 'string' ? item : (item as any).itemId || (item as any).id] as number | undefined,
          }));
          
          useSessionReportStore.getState().createRankingReport({
            cycleId: snapshot.cycleId,
            focusListId: snapshot.focusListId || '',
            ranking,
            toolUsed: snapshot.toolName,
            toolConfig: snapshot.toolConfig,
            createdBy: userId || snapshot.createdBy || 'system',
          }).then(report => {
            if (report) {
              console.log('[ArrangeStore] Created ranking report:', report.id);
            }
          }).catch(err => {
            console.warn('[ArrangeStore] Ranking report creation failed (non-blocking):', err);
          });
        }

        return { success: true };
      } catch (err) {
        console.error('[ArrangeStore] Save exception:', err);
        return { success: false, error: err instanceof Error ? err.message : 'Unknown error' };
      }
    }
    
    return { success: true };
  },

  /**
   * Create a ranking override with provenance
   * Per Doc 07: Overrides create new reports, do not mutate original
   */
  createRankingOverride: async (params) => {
    const { originalSnapshotId, newRankedItems, rationale, overriddenBy, cycleId } = params;
    
    // Find original snapshot
    const original = get().snapshots.find(s => s.id === originalSnapshotId);
    if (!original) {
      return { success: false, error: 'Original snapshot not found' };
    }

    // Validate rationale is provided
    if (!rationale || rationale.trim().length === 0) {
      return { success: false, error: 'Rationale is required for overrides' };
    }

    const now = new Date();
    
    // Create new snapshot with override metadata
    const overrideSnapshot: ArrangeSnapshot = {
      id: uuidv4(),
      sessionId: original.sessionId,
      cycleId: cycleId || original.cycleId,
      focusListId: original.focusListId,
      toolName: original.toolName,
      toolConfig: original.toolConfig,
      lens: original.lens,
      rankedItems: newRankedItems,
      scores: original.scores, // Keep original scores
      cutLinePosition: original.cutLinePosition,
      createdAt: now,
      createdBy: overriddenBy,
      // Store override metadata
      overrideOf: originalSnapshotId,
      overrideRationale: rationale,
    };

    // Add to local state
    set((state) => ({
      snapshots: [overrideSnapshot, ...state.snapshots],
      lastToolResult: { snapshot: overrideSnapshot, timestamp: Date.now() },
    }));

    // Persist to Supabase
    if (isSupabaseAvailable()) {
      try {
        const supabase = getSafeSupabaseClient();
        if (!supabase) return { success: false, error: 'Supabase not available' };
        
        const workspaceId = getCurrentWorkspaceId();
        const dbRecord = snapshotToDb(overrideSnapshot, workspaceId);
        
        // Add override metadata to the record
        (dbRecord as any).override_of = originalSnapshotId;
        (dbRecord as any).override_rationale = rationale;
        
        const { error } = await supabase
          .from(TABLE_NAME)
          .insert(dbRecord);
        
        if (error) {
          console.error('[ArrangeStore] Failed to save override snapshot:', error);
          return { success: false, error: error.message };
        }
        
        console.log('[ArrangeStore] Saved override snapshot:', overrideSnapshot.id, 'overriding:', originalSnapshotId);

        // Emit governance event for ranking override (non-blocking)
        const eventCycleId = cycleId || original.cycleId;
        if (eventCycleId) {
          supabase.from('governance_events').insert({
            id: generateUUID(),
            cycle_id: eventCycleId,
            event_type: 'ranking_overridden',
            actor_id: overriddenBy,
            occurred_at: now.toISOString(),
            note: JSON.stringify({
              originalSnapshotId,
              newSnapshotId: overrideSnapshot.id,
              rationale,
              itemCount: newRankedItems.length,
            }),
          }).then(({ error: govError }) => {
            if (govError) {
              console.warn('[ArrangeStore] Governance event failed (non-blocking):', govError.message);
            }
          });
        }

        return { success: true, newSnapshot: overrideSnapshot };
      } catch (err) {
        console.error('[ArrangeStore] Override exception:', err);
        return { success: false, error: err instanceof Error ? err.message : 'Unknown error' };
      }
    }

    return { success: true, newSnapshot: overrideSnapshot };
  },
  
  clearSnapshots: () => set({ snapshots: [] }),
}));


